'use client'

import { createContext, PropsWithChildren, useContext, useRef } from 'react'

import { useDialog } from './dialog'
import { MoveToSignInDialog } from './move-to-sign-in-dialog'

export const DIALOG = {
  MOVE_TO_SIGN_IN: 'MOVE_TO_SIGN_IN',
} as const

type CommonDialog = keyof typeof DIALOG

type DialogState = {
  openMoveToSignInDialog: (pathname?: string) => void
  open: (key: CommonDialog) => void
  close: (key: CommonDialog) => void
}

const DialogContext = createContext<DialogState | null>(null)

export const useCommonDialog = () => {
  const context = useContext(DialogContext)

  if (!context) {
    throw new Error(
      'useCommonDialog must be used within a CommonDialogProvider',
    )
  }

  return context
}

export const CommonDialogProvider = ({ children }: PropsWithChildren) => {
  const pathnameRef = useRef<string | null>(null)
  const { isOpen, open, close } = useDialog<CommonDialog>()

  const openMoveToSignInDialog = (pathname?: string) => {
    if (pathname) {
      pathnameRef.current = pathname
    }

    open(DIALOG.MOVE_TO_SIGN_IN)
  }

  const closeDialog = (key: CommonDialog) => () => {
    close(key)

    if (key === DIALOG.MOVE_TO_SIGN_IN) {
      pathnameRef.current = null
    }
  }

  return (
    <DialogContext.Provider value={{ open, close, openMoveToSignInDialog }}>
      {children}

      <MoveToSignInDialog
        isOpen={isOpen(DIALOG.MOVE_TO_SIGN_IN)}
        onOpenChange={closeDialog(DIALOG.MOVE_TO_SIGN_IN)}
        pathname={pathnameRef.current}
      />
    </DialogContext.Provider>
  )
}
